import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { StepProps, Products, SizeQuantities, TeamInfo, ProductKey, ProductLogo, JerseyTeamSizes, PantsTeamSizes } from '../../../types';
import {
  JERSEY_TYPES,
  PANTS_TYPES,
  SOCKS_TYPES,
  CAPS_TYPES,
  JERSEY_YOUTH_SIZES,
  ADULT_SIZES,
  PANTS_YOUTH_SIZES,
  COACH_SIZES,
  SOCK_SIZE_LABELS,
  getAllTeams,
} from '../../../utils/formHelpers';

// Custom size grid that takes size labels directly
interface CustomSizeGridProps {
  sizes: SizeQuantities;
  sizeLabels: string[];
  sizeLabelMap?: Record<string, string>;
  onSizeChange: (size: string, qty: number) => void;
  label?: string;
}

const CustomSizeGrid: React.FC<CustomSizeGridProps> = ({ sizes, sizeLabels, sizeLabelMap, onSizeChange, label }) => {
  const totalQty = Object.values(sizes || {}).reduce((sum, qty) => sum + (parseInt(String(qty)) || 0), 0);

  return (
    <div className="size-grid">
      <div className="size-grid-header">
        <span className="size-grid-label">{label || 'Sizes'}</span>
        <span className="size-grid-total">Total: {totalQty}</span>
      </div>
      <div className="size-grid-inputs">
        {sizeLabels.map(size => (
          <div key={size} className="size-input-group">
            <label className="size-label" title={sizeLabelMap?.[size] || size}>
              {sizeLabelMap?.[size] ? size : size}
            </label>
            <input
              type="number"
              min="0"
              className="size-input"
              value={sizes?.[size] || 0}
              onChange={(e) => onSizeChange(size, parseInt(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface LogoUploadProps {
  logo?: ProductLogo;
  onUpload: (file: File | null) => void;
  label?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({ logo, onUpload, label }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onUpload(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpload(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0] || null;
    if (file && file.type.startsWith('image/')) {
      onUpload(file);
    }
  };

  return (
    <div className="logo-upload-section">
      <label className="form-label">{label || 'Logo (Optional)'}</label>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      {logo ? (
        <div className="logo-preview">
          <img src={logo.data} alt="Uploaded logo" className="logo-preview-image" />
          <div className="logo-preview-info">
            <span className="logo-preview-name">{logo.name}</span>
            <span className="logo-preview-size">
              {logo.size < 1024 ? logo.size + ' B' :
               logo.size < 1024 * 1024 ? (logo.size / 1024).toFixed(1) + ' KB' :
               (logo.size / (1024 * 1024)).toFixed(1) + ' MB'}
            </span>
          </div>
          <button type="button" className="logo-remove-btn" onClick={handleRemove} title="Remove logo">
            &times;
          </button>
        </div>
      ) : (
        <div
          className="logo-upload-area"
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="logo-upload-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <p className="logo-upload-text">
            <span className="logo-upload-link">Click to upload</span> or drag and drop
          </p>
          <p className="logo-upload-hint">PNG, JPG, GIF, SVG or WebP (max 5MB)</p>
        </div>
      )}
    </div>
  );
};

const ProductsStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { products, divisions } = formData;
  const [selectedTeam, setSelectedTeam] = useState<TeamInfo | null>(null);
  const [activeTab, setActiveTab] = useState<ProductKey>('jerseys');

  const allTeams = getAllTeams(divisions);

  // Auto-select first team if none selected
  useEffect(() => {
    if (!selectedTeam && allTeams.length > 0) {
      setSelectedTeam(allTeams[0]);
    }
  }, [allTeams, selectedTeam]);

  // Get empty sizes for jersey (youth/adult/coach structure)
  const getEmptyJerseySizes = (): JerseyTeamSizes => ({
    youth: Object.fromEntries(JERSEY_YOUTH_SIZES.map(s => [s, 0])),
    adult: Object.fromEntries(ADULT_SIZES.map(s => [s, 0])),
    coach: Object.fromEntries(COACH_SIZES.map(s => [s, 0])),
  });

  // Get empty sizes for pants (youth/adult structure)
  const getEmptyPantsSizes = (): PantsTeamSizes => ({
    youth: Object.fromEntries(PANTS_YOUTH_SIZES.map(s => [s, 0])),
    adult: Object.fromEntries(ADULT_SIZES.map(s => [s, 0])),
  });

  // Get empty sizes for socks
  const getEmptySockSizes = (): SizeQuantities =>
    Object.fromEntries(Object.keys(SOCK_SIZE_LABELS).map(s => [s, 0]));

  // Get empty sizes for belts/caps
  const getEmptyBeltCapSizes = (): SizeQuantities => ({ Youth: 0, Adult: 0 });

  // Calculate team total for display
  const getTeamTotal = (teamKey: string): number => {
    let total = 0;

    // Jerseys (nested structure)
    const jerseySizes = products.jerseys.teamSizes?.[teamKey] as JerseyTeamSizes | undefined;
    if (jerseySizes) {
      total += Object.values(jerseySizes.youth || {}).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
      total += Object.values(jerseySizes.adult || {}).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
      total += Object.values(jerseySizes.coach || {}).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
    }

    // Pants (nested structure)
    const pantsSizes = products.pants.teamSizes?.[teamKey] as PantsTeamSizes | undefined;
    if (pantsSizes) {
      total += Object.values(pantsSizes.youth || {}).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
      total += Object.values(pantsSizes.adult || {}).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
    }

    // Belts, socks, caps (flat structure)
    ['belts', 'socks', 'caps'].forEach(key => {
      const sizes = products[key as ProductKey].teamSizes?.[teamKey] as SizeQuantities | undefined;
      if (sizes) {
        total += Object.values(sizes).reduce((s, q) => s + (parseInt(String(q)) || 0), 0);
      }
    });

    return total;
  };

  // Check if a team has any products configured
  const teamHasProducts = (teamKey: string): boolean => {
    return getTeamTotal(teamKey) > 0;
  };

  // Update product settings (type, notes)
  const updateProduct = (productKey: ProductKey, field: string, value: string): void => {
    const newProducts: Products = {
      ...products,
      [productKey]: {
        ...products[productKey],
        [field]: value
      }
    };
    updateFormData({ products: newProducts });
  };

  // Update jersey sizes (youth/adult/coach)
  const updateJerseySize = (teamKey: string, category: 'youth' | 'adult' | 'coach', size: string, quantity: number): void => {
    const currentTeamSizes = (products.jerseys.teamSizes?.[teamKey] as JerseyTeamSizes) || getEmptyJerseySizes();

    const newProducts: Products = {
      ...products,
      jerseys: {
        ...products.jerseys,
        teamSizes: {
          ...products.jerseys.teamSizes,
          [teamKey]: {
            ...currentTeamSizes,
            [category]: {
              ...currentTeamSizes[category],
              [size]: quantity
            }
          }
        }
      }
    };
    updateFormData({ products: newProducts });
  };

  // Update pants sizes (youth/adult)
  const updatePantsSize = (teamKey: string, category: 'youth' | 'adult', size: string, quantity: number): void => {
    const currentTeamSizes = (products.pants.teamSizes?.[teamKey] as PantsTeamSizes) || getEmptyPantsSizes();

    const newProducts: Products = {
      ...products,
      pants: {
        ...products.pants,
        teamSizes: {
          ...products.pants.teamSizes,
          [teamKey]: {
            ...currentTeamSizes,
            [category]: {
              ...currentTeamSizes[category],
              [size]: quantity
            }
          }
        }
      }
    };
    updateFormData({ products: newProducts });
  };

  // Update flat sizes (belts, socks, caps)
  const updateFlatSize = (productKey: ProductKey, teamKey: string, size: string, quantity: number): void => {
    const getEmptyForProduct = () => {
      if (productKey === 'socks') return getEmptySockSizes();
      return getEmptyBeltCapSizes();
    };

    const currentSizes = (products[productKey].teamSizes?.[teamKey] as SizeQuantities) || getEmptyForProduct();

    const newProducts: Products = {
      ...products,
      [productKey]: {
        ...products[productKey],
        teamSizes: {
          ...products[productKey].teamSizes,
          [teamKey]: {
            ...currentSizes,
            [size]: quantity
          }
        }
      }
    };
    updateFormData({ products: newProducts });
  };

  // Handle logo upload for a product
  const handleLogoUpload = (productKey: ProductKey, file: File | null): void => {
    if (!file) {
      const newProducts: Products = {
        ...products,
        [productKey]: {
          ...products[productKey],
          logo: undefined
        }
      };
      updateFormData({ products: newProducts });
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, SVG, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const logo: ProductLogo = {
        data: e.target?.result as string,
        name: file.name,
        size: file.size
      };
      const newProducts: Products = {
        ...products,
        [productKey]: {
          ...products[productKey],
          logo
        }
      };
      updateFormData({ products: newProducts });
    };
    reader.readAsDataURL(file);
  };

  // Render Jerseys Tab with Youth/Adult/Coach sections
  const renderJerseysTab = (): React.ReactNode => {
    if (!selectedTeam) return null;
    const teamKey = selectedTeam.key;
    const teamSizes = (products.jerseys.teamSizes?.[teamKey] as JerseyTeamSizes) || getEmptyJerseySizes();

    return (
      <div className="product-tab-content">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Jersey Style</label>
            <select
              className="form-input"
              value={products.jerseys.type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => updateProduct('jerseys', 'type', e.target.value)}
            >
              {JERSEY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Product SKU (Optional)</label>
            <input
              type="text"
              className="form-input"
              value={products.jerseys.sku || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateProduct('jerseys', 'sku', e.target.value)}
              placeholder="e.g., JER-2026-001"
            />
          </div>
        </div>

        <div className="size-category">
          <h5 className="size-category-title">Youth Sizes</h5>
          <CustomSizeGrid
            sizes={teamSizes.youth || {}}
            sizeLabels={JERSEY_YOUTH_SIZES}
            onSizeChange={(size, qty) => updateJerseySize(teamKey, 'youth', size, qty)}
            label={`Youth - ${selectedTeam.teamName}`}
          />
        </div>

        <div className="size-category">
          <h5 className="size-category-title">Adult Sizes</h5>
          <CustomSizeGrid
            sizes={teamSizes.adult || {}}
            sizeLabels={ADULT_SIZES}
            onSizeChange={(size, qty) => updateJerseySize(teamKey, 'adult', size, qty)}
            label={`Adult - ${selectedTeam.teamName}`}
          />
        </div>

        <div className="size-category">
          <h5 className="size-category-title">Coach Sizes</h5>
          <CustomSizeGrid
            sizes={teamSizes.coach || {}}
            sizeLabels={COACH_SIZES}
            onSizeChange={(size, qty) => updateJerseySize(teamKey, 'coach', size, qty)}
            label={`Coach - ${selectedTeam.teamName}`}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            value={products.jerseys.notes}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateProduct('jerseys', 'notes', e.target.value)}
            rows={2}
            placeholder="Numbering, names, colors..."
          />
        </div>

        <LogoUpload
          logo={products.jerseys.logo}
          onUpload={(file) => handleLogoUpload('jerseys', file)}
          label="Jersey Logo (Optional)"
        />
      </div>
    );
  };

  // Render Pants Tab with Youth/Adult sections
  const renderPantsTab = (): React.ReactNode => {
    if (!selectedTeam) return null;
    const teamKey = selectedTeam.key;
    const teamSizes = (products.pants.teamSizes?.[teamKey] as PantsTeamSizes) || getEmptyPantsSizes();

    return (
      <div className="product-tab-content">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Pants Style</label>
            <select
              className="form-input"
              value={products.pants.type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => updateProduct('pants', 'type', e.target.value)}
            >
              {PANTS_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Product SKU (Optional)</label>
            <input
              type="text"
              className="form-input"
              value={products.pants.sku || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateProduct('pants', 'sku', e.target.value)}
              placeholder="e.g., PNT-2026-001"
            />
          </div>
        </div>

        <div className="size-category">
          <h5 className="size-category-title">Youth Sizes</h5>
          <CustomSizeGrid
            sizes={teamSizes.youth || {}}
            sizeLabels={PANTS_YOUTH_SIZES}
            onSizeChange={(size, qty) => updatePantsSize(teamKey, 'youth', size, qty)}
            label={`Youth - ${selectedTeam.teamName}`}
          />
        </div>

        <div className="size-category">
          <h5 className="size-category-title">Adult Sizes</h5>
          <CustomSizeGrid
            sizes={teamSizes.adult || {}}
            sizeLabels={ADULT_SIZES}
            onSizeChange={(size, qty) => updatePantsSize(teamKey, 'adult', size, qty)}
            label={`Adult - ${selectedTeam.teamName}`}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            value={products.pants.notes}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateProduct('pants', 'notes', e.target.value)}
            rows={2}
            placeholder="Color preferences..."
          />
        </div>
      </div>
    );
  };

  // Render Belts Tab (per team - Youth/Adult quantities)
  const renderBeltsTab = (): React.ReactNode => {
    if (!selectedTeam) return null;
    const teamKey = selectedTeam.key;
    const teamSizes = (products.belts.teamSizes?.[teamKey] as SizeQuantities) || getEmptyBeltCapSizes();

    return (
      <div className="product-tab-content">
        <CustomSizeGrid
          sizes={teamSizes}
          sizeLabels={['Youth', 'Adult']}
          onSizeChange={(size, qty) => updateFlatSize('belts', teamKey, size, qty)}
          label={`Belt Quantities for ${selectedTeam.teamName}`}
        />
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            value={products.belts.notes}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateProduct('belts', 'notes', e.target.value)}
            rows={2}
            placeholder="Colors needed..."
          />
        </div>
      </div>
    );
  };

  // Render Socks Tab (per team with proper sock sizes)
  const renderSocksTab = (): React.ReactNode => {
    if (!selectedTeam) return null;
    const teamKey = selectedTeam.key;
    const teamSizes = (products.socks.teamSizes?.[teamKey] as SizeQuantities) || getEmptySockSizes();

    return (
      <div className="product-tab-content">
        <div className="form-group">
          <label className="form-label">Sock Style</label>
          <select
            className="form-input"
            value={products.socks.type}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => updateProduct('socks', 'type', e.target.value)}
          >
            {SOCKS_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="sock-sizes-section">
          <p className="size-help-text">Sock sizes with descriptions:</p>
          <CustomSizeGrid
            sizes={teamSizes}
            sizeLabels={Object.keys(SOCK_SIZE_LABELS)}
            sizeLabelMap={SOCK_SIZE_LABELS}
            onSizeChange={(size, qty) => updateFlatSize('socks', teamKey, size, qty)}
            label={`Sock Quantities for ${selectedTeam.teamName}`}
          />
          <div className="sock-size-legend">
            {Object.entries(SOCK_SIZE_LABELS).map(([key, label]) => (
              <div key={key} className="sock-size-legend-item">
                <strong>{key}:</strong> {label}
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            value={products.socks.notes}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateProduct('socks', 'notes', e.target.value)}
            rows={2}
            placeholder="Colors, patterns..."
          />
        </div>
      </div>
    );
  };

  // Render Caps Tab (per team - Youth/Adult quantities)
  const renderCapsTab = (): React.ReactNode => {
    if (!selectedTeam) return null;
    const teamKey = selectedTeam.key;
    const teamSizes = (products.caps.teamSizes?.[teamKey] as SizeQuantities) || getEmptyBeltCapSizes();

    return (
      <div className="product-tab-content">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Cap Style</label>
            <select
              className="form-input"
              value={products.caps.type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => updateProduct('caps', 'type', e.target.value)}
            >
              {CAPS_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Product SKU (Optional)</label>
            <input
              type="text"
              className="form-input"
              value={products.caps.sku || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateProduct('caps', 'sku', e.target.value)}
              placeholder="e.g., CAP-2026-001"
            />
          </div>
        </div>

        <CustomSizeGrid
          sizes={teamSizes}
          sizeLabels={['Youth', 'Adult']}
          onSizeChange={(size, qty) => updateFlatSize('caps', teamKey, size, qty)}
          label={`Cap Quantities for ${selectedTeam.teamName}`}
        />

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            value={products.caps.notes}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateProduct('caps', 'notes', e.target.value)}
            rows={2}
            placeholder="Embroidery details, placement..."
          />
        </div>

        <LogoUpload
          logo={products.caps.logo}
          onUpload={(file) => handleLogoUpload('caps', file)}
          label="Cap Logo (Optional)"
        />
      </div>
    );
  };

  return (
    <div className="step-content products-step">
      <div className="step-section">
        <h3 className="step-section-title">Configure Products</h3>
        <p className="step-section-description">
          Select a team from the list, then configure their products. Jerseys and pants have Youth, Adult, and Coach sizing options.
        </p>

        {allTeams.length === 0 ? (
          <div className="no-teams-alert">
            <p>No teams added yet. Go back to the previous step to add divisions and teams.</p>
          </div>
        ) : (
          <div className="products-layout">
            {/* Team Sidebar */}
            <div className="team-sidebar">
              <h4 className="sidebar-title">Teams</h4>
              <ul className="team-list">
                {allTeams.map((team) => (
                  <li
                    key={team.key}
                    className={`team-list-item ${selectedTeam?.key === team.key ? 'active' : ''} ${teamHasProducts(team.key) ? 'has-products' : ''}`}
                    onClick={() => setSelectedTeam(team)}
                  >
                    <div className="team-list-info">
                      <span className="team-list-name">{team.teamName}</span>
                      <span className="team-list-division">{team.divisionName}</span>
                    </div>
                    {teamHasProducts(team.key) && (
                      <span className="team-list-badge">{getTeamTotal(team.key)}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Product Configuration Area */}
            <div className="product-config-area">
              {selectedTeam ? (
                <>
                  <div className="config-header">
                    <h4 className="config-team-name">{selectedTeam.teamName}</h4>
                    <span className="config-division">{selectedTeam.divisionName}</span>
                  </div>

                  {/* Product Tabs */}
                  <div className="product-tabs">
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'jerseys' ? 'active' : ''}`}
                      onClick={() => setActiveTab('jerseys')}
                    >
                      Jerseys
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'pants' ? 'active' : ''}`}
                      onClick={() => setActiveTab('pants')}
                    >
                      Pants
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'belts' ? 'active' : ''}`}
                      onClick={() => setActiveTab('belts')}
                    >
                      Belts
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'socks' ? 'active' : ''}`}
                      onClick={() => setActiveTab('socks')}
                    >
                      Socks
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'caps' ? 'active' : ''}`}
                      onClick={() => setActiveTab('caps')}
                    >
                      Caps
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="product-tab-panel">
                    {activeTab === 'jerseys' && renderJerseysTab()}
                    {activeTab === 'pants' && renderPantsTab()}
                    {activeTab === 'belts' && renderBeltsTab()}
                    {activeTab === 'socks' && renderSocksTab()}
                    {activeTab === 'caps' && renderCapsTab()}
                  </div>
                </>
              ) : (
                <div className="no-team-selected">
                  <p>Select a team from the list to configure their products.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsStep;
